import React, { useEffect, useState } from 'react'
import { Box, Grid } from '@mui/material'
import { Loader } from '../../indicators'
import { isEmpty, noop, toLower } from 'lodash'
import { useLocatorApi } from '../../../local'
import { SimpleMap } from './SimpleMap'
import { RepLocatorFilters } from './ RepLocatorFilters'
import { RepLocatorList } from './RepLocatorList'
import { useSelector } from '@ggs/store'
import { State } from 'country-state-list'

import './RepLocatorView.scss'

/**
 * @typedef {import('../../../definitions').RepResponse} RepResponse
 */

/**
 * Rep locator view.
 * @return {JSX.Element}
 */
export const RepLocatorView = () => {
  const { getReps } = useLocatorApi()
  const selectedCountry = useSelector((state) => state.settings?.country || 'CA')
  const selectedStateProvince = useSelector((state) => state.settings?.stateProvince || '')
  /**
   * @type {RepResponse}
   */
  const defaultReps = {
    salesDirectors: [],
    showRoom: null,
    territories: {
      id: null,
      name: null,
      salesReps: [],
      subTerritories: []
    }
  }
  /**
   * @type {RepResponse}
   */
  const emptyReps = null
  const [reps, setReps] = useState(emptyReps)
  const [loading, setLoading] = useState(true)
  const [responseLoading, setResponseLoading] = useState(false)

  /**
   * @typedef {Object} RepsFilters
   * @property {string} countryCode
   * @property {string=} stateProvinceCode
   */
  /**
   * Compose reps filter path string params for API request.
   * @param {RepsFilters} filters
   * @return {string}
   */
  const composeRepsFilterParams = ({ countryCode, stateProvinceCode = '' }) => {
    /**
     *  @type {Object<string, string>}
     */
    const countryMap = {
      CA: 'Canada',
      US: 'USA',
      ASI: 'Asia',
      CAR: 'Caribbean',
      EU: 'Europe',
      MENA: 'MENA'
    }

    const country = countryMap[countryCode] || countryCode
    let stateProvince = ''
    if (stateProvinceCode !== 'none' && !isEmpty(stateProvinceCode)) {
      const stateProvinceObject = State.getStateByCodeAndCountry(stateProvinceCode, countryCode)
      if (stateProvinceObject.name) {
        stateProvince = `${stateProvinceObject.name}/`
      }
    }
    return toLower(`${stateProvince}${country}`)
  }

  /**
   * Resolve reps data from Location API.
   * @param {RepsFilters} filters
   * @param {function} setSubmitting
   * @return {Promise<void>}
   */
  const resolveRepsData = async (filters, setSubmitting = noop) => {
    const pathParams = composeRepsFilterParams(filters)
    const repResults = await getReps(pathParams)
    setSubmitting(false)

    if (repResults) {
      setReps(repResults)
    } else {
      setReps(defaultReps)
    }

    setLoading(false)
    setResponseLoading(false)
  }

  // Update sales data after country changed.
  useEffect(() => {
    if (!selectedStateProvince && (selectedCountry === 'US' || selectedCountry === 'CA')) {
      if (reps) {
        setReps(emptyReps)
      }

      if (loading === true) {
        setLoading(false)
      }
    } else {
      setResponseLoading(true)
      resolveRepsData({
        countryCode: selectedCountry,
        stateProvinceCode: selectedStateProvince,
      }).then(() => {})
    }
  }, [selectedCountry, selectedStateProvince])

  return (
    <Box className="rep-locator-view" id="rep-locator-view">
      {loading ? <Loader />
        : (
          <>
            <Grid className="rep-locator-view__container">
              <Grid className="rep-locator-view__map-wrapper">
                <SimpleMap />
              </Grid>
              <Grid className="rep-locator-view__filters">
                <RepLocatorFilters />
              </Grid>
            </Grid>
            {responseLoading ? <Loader />
              : <RepLocatorList reps={reps} />
            }
          </>
        )
      }
    </Box>
  )
}
